import {
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { AddRounded, ClearRounded, CloseRounded } from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker } from '../../components/CustomDatePicker';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { ProjectDocumentRevision } from '../../api/project-document-revisions';
import { CreateProjectDocument } from '../../api/project-documents';
import { RootState } from '../../app/store';
import { ProductNameLabel } from '../../common/enum/ProductNameLabel';
import HelpTooltip from '../../components/HelpTooltip';
import { DAYS_FOR_COMPLETE_REVIEW } from '../../constants';
import { addWeekdays } from '../../functions/addWeekdays';
import { useAppDispatch } from '../../hooks/useAppRudux';
import useMasterDateForDocument from '../../hooks/useMasterDateForDocument';
import useMasterDateForDocumentRevision from '../../hooks/useMasterDateForDocumentRevision';
import { useQueryStringNumber } from '../../hooks/useQueryString';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import { getProjectAsync } from '../Project/projectSlice';
import { createProjectDocumentRevisionAsync } from '../ProjectDocumentRevision/projectDocumentRevisionSlice';
import { PurposeLabel } from './BulkPlanning';
import { createProjectDocumentAsync } from './projectDocumentSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    additionalNoCard: {
      padding: theme.spacing(1, 2, 1.5, 2),
      borderRadius: theme.spacing(1)
    },
    additionalNoField: {
      display: 'flex',
      '& > *': {
        marginRight: theme.spacing(2),
        '&:last-child': {
          marginRight: theme.spacing(0)
        }
      },
      [theme.breakpoints.down('sm')]: {
        display: 'block',
        '& > *': {
          marginBottom: theme.spacing(1.5),
          '&:last-child': {
            marginBottom: theme.spacing(0)
          }
        }
      }
    },
    hoverFocus: {
      border: '2px solid',
      borderColor: theme.palette.background.default,
      borderRadius: 16,
      transition: '0.4s',
      '&:hover': {
        borderColor: theme.palette.primary.main
      }
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    dividerRevision: {
      margin: theme.spacing(3, 0, 2, 0)
    },
    fileNotFound: {
      padding: theme.spacing(12.25, 2),
      textAlign: 'center'
    },
    buttonStepOne: {
      display: 'flex',
      '& > :nth-child(1)': {
        width: '50%',
        marginRight: theme.spacing(1)
      }
    },
    buttonNewRev: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(2)
    },
    fullWidth: {
      width: '100%'
    },
    padding1: {
      padding: theme.spacing(1)
    },
    marginTopNone: {
      marginTop: 0
    },
    marginRightNone: {
      marginRight: 0
    },
    marginBottom: {
      marginBottom: theme.spacing(2)
    },
    textAlignRight: {
      textAlign: 'right'
    },
    positionRelative: {
      position: 'relative'
    },
    closeIcon: {
      position: 'absolute',
      right: theme.spacing(0.5),
      top: theme.spacing(0.5),
      zIndex: 10
    }
  })
);

export interface ProjectDocumentRevisionWithTempId
  extends ProjectDocumentRevision {
  tempId: number;
}

export default function DocumentCreate() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const projectId = useQueryStringNumber({ query: 'projectId' });
  const [contractors, disciplines, documents, users] =
    useMasterDateForDocument();
  const [purposes] = useMasterDateForDocumentRevision();

  const { data: accessControls } = useSelector(
    (state: RootState) => state.accessControls
  );
  const { createdProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const [currentDocId, setCurrentDocId] = useState<number | null>(null);
  const [docRegis, setDocRegis] = useState<CreateProjectDocument>({
    projectId,
    discipline: null,
    document: null,
    no: '',
    name: '',
    consultant: null,
    contractor: null,
    subContractor: null,
    clientDocumentNo: null,
    epcDocumentNo: null,
    vendorDocumentNo: null,
    accessControl: null,
    documentCreator: null,
    isAIMS: false,
    aimsDocumentNo: null,
    customAccessControlUsers: []
  });
  const [customShare, setCustomShare] = useState<boolean>(false);

  const defaultRevision = {
    purpose: null,
    revision: null,
    manHour: null,
    planIssueDate: new Date(),
    planCompletedDate: addWeekdays(new Date(), DAYS_FOR_COMPLETE_REVIEW),
    projectDocumentId: currentDocId,
    isAutoApproval: false
  };
  const [tempId, setTempId] = useState(0);
  const [revRegis, setRevRegis] = useState<ProjectDocumentRevisionWithTempId[]>(
    []
  );

  const steps = [t('projectDocument.documentInformation'), t('projectDocument.newDocumentRevision')];
  const [activeStep, setActiveStep] = useState<number>(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (selectedProject) {
      const repcoId = 7;
      const repco =
        contractors.find((contractor) => contractor.id === repcoId) ?? null;
      // Magic for selecting REPCO as:
      switch (checkProductType(selectedProject.productName)) {
        // 1. Consultant on PMC project
        case ProductNameLabel.pmc:
          setDocRegis({ ...docRegis, consultant: repco });
          break;
        // 2. Main contractor on EPC project
        case ProductNameLabel.epc:
          setDocRegis({ ...docRegis, contractor: repco });
          break;
        default:
          break;
      }
    }
  }, [selectedProject, contractors]);

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectAsync(projectId));
    }
  }, []);

  useEffect(() => {
    if (createdProjectDocument) {
      setCurrentDocId(createdProjectDocument.id);
      setRevRegis([
        {
          ...defaultRevision,
          projectDocumentId: createdProjectDocument.id,
          tempId
        }
      ]);
      setTempId(tempId + 1);
    }
  }, [createdProjectDocument]);

  function isStepOptional(step: number) {
    return step === 1;
  }

  function isStepSkipped(step: number) {
    return skipped.has(step);
  }

  function handleDeleteRevision(id: number) {
    const newRevision = revRegis?.filter((revision) => revision.tempId !== id);
    setRevRegis(newRevision);
  }

  function handleChangeValueById(
    id: number,
    fieldName: keyof ProjectDocumentRevisionWithTempId,
    newValue: any
  ) {
    const revisionIndex = revRegis.findIndex(
      (revision) => revision.tempId === id
    );
    const newRevision = { ...revRegis[revisionIndex], [fieldName]: newValue };
    if (fieldName === 'purpose') {
      newRevision.revision = null;
      newRevision.isAutoApproval = false;
    }
    if (fieldName === 'planIssueDate') {
      newRevision.planCompletedDate = addWeekdays(
        newValue,
        DAYS_FOR_COMPLETE_REVIEW
      );
    }
    setRevRegis([
      ...revRegis.slice(0, revisionIndex),
      newRevision,
      ...revRegis.slice(revisionIndex + 1)
    ]);
  }

  async function onSubmitDocument(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      const result = await dispatch(createProjectDocumentAsync(docRegis));
      await unwrapResult(result);
      /*-- Step tracking --*/
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      /*-- End --*/
      dispatch(
        loadingSlice.actions.success({
          message: t('messages.success.documentCreated') as string
        })
      );
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  async function onSubmitDocumentRevision(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();
    try {
      setLoading(true);
      const result = await dispatch(
        createProjectDocumentRevisionAsync(revRegis)
      );
      await unwrapResult(result);
      /*-- Step tracking --*/
      let newSkipped = skipped;
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setSkipped(newSkipped);
      /*-- End --*/
      dispatch(
        loadingSlice.actions.success({
          message: t('messages.success.revisionsCreated') as string
        })
      );
      setTimeout(() => {
        history.push(`/projects/${projectId}`);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  function onSkipDocumentRevision() {
    /*-- Step tracking --*/
    if (!isStepOptional(activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error(t('projectDocument.errors.cantSkipNonOptionalStep') as string);
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
    /*-- End --*/
    history.push(`/projects/${projectId}`);
  }

  if (!projectId) {
    return <Redirect to="/projects" />;
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="createDocument"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectDocument.newDocument')}
      </Typography>
      <Stepper
        className={classes.card}
        component={Paper}
        activeStep={activeStep}
        alternativeLabel
      >
        {steps.map((label, index) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: { optional?: React.ReactNode } = {};
          if (isStepOptional(index)) {
            labelProps.optional = (
              <Typography variant="body2" align="center">
                {t('projectDocument.optional')}
              </Typography>
            );
          }
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <form onSubmit={onSubmitDocument}>
        <Paper className={classes.card}>
          <Typography variant="subtitle1" gutterBottom>
            {t('projectDocument.documentInformation')}
          </Typography>
          <Divider className={classes.divider} />
          <Collapse in={activeStep === 0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <Autocomplete
                  className={classes.fullWidth}
                  id="discipline"
                  options={disciplines}
                  getOptionLabel={(option) => option?.name}
                  value={docRegis.discipline}
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    setDocRegis({
                      ...docRegis,
                      discipline: newValue,
                      document: null,
                      no: '',
                      name: '',
                      clientDocumentNo: null,
                      epcDocumentNo: null,
                      vendorDocumentNo: null
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label={t('projectDocument.discipline')}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <Autocomplete
                  className={classes.fullWidth}
                  id="document-template"
                  options={documents
                    ?.filter(
                      (document) =>
                        !docRegis.discipline ||
                        document.disciplineId === docRegis.discipline?.id
                    )
                    .sort((a, b) => -b.name.localeCompare(a.name))}
                  getOptionLabel={(option) => option?.name}
                  value={docRegis.document}
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    const discipline = disciplines.find(
                      (discipline) => discipline.id === newValue?.disciplineId
                    );
                    setDocRegis({
                      ...docRegis,
                      discipline: discipline ? discipline : docRegis.discipline,
                      document: newValue,
                      no: newValue ? newValue.no : '',
                      name: newValue ? newValue.name : '',
                      clientDocumentNo: null,
                      epcDocumentNo: null,
                      vendorDocumentNo: null
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label={t('projectDocument.documentTemplate')}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  className={classes.fullWidth}
                  id="document-no"
                  variant="outlined"
                  size="small"
                  label={t('projectDocument.documentNo')}
                  disabled={!docRegis.document}
                  value={docRegis.no}
                  onChange={(event) => {
                    setDocRegis({
                      ...docRegis,
                      no: event.target.value
                    });
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  className={classes.fullWidth}
                  id="document-name"
                  variant="outlined"
                  size="small"
                  label={t('projectDocument.documentName')}
                  disabled={!docRegis.document}
                  value={docRegis.name}
                  onChange={(event) => {
                    setDocRegis({
                      ...docRegis,
                      name: event.target.value
                    });
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  className={classes.fullWidth}
                  id="consultant"
                  options={contractors}
                  getOptionLabel={(option) => option?.name}
                  value={docRegis.consultant}
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    setDocRegis({
                      ...docRegis,
                      consultant: newValue
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label={t('projectDocument.consultant')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  className={classes.fullWidth}
                  id="contractor"
                  options={contractors}
                  getOptionLabel={(option) => option?.name}
                  value={docRegis.contractor}
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    setDocRegis({
                      ...docRegis,
                      contractor: newValue
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label={t('projectDocument.mainContractor')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  className={classes.fullWidth}
                  id="sub-contractor"
                  options={contractors}
                  getOptionLabel={(option) => option?.name}
                  value={docRegis.subContractor}
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    setDocRegis({
                      ...docRegis,
                      subContractor: newValue
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label={t('projectDocument.subContractor')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <Autocomplete
                  className={classes.fullWidth}
                  id="access-control"
                  options={accessControls}
                  getOptionLabel={(option) => option?.name}
                  value={docRegis.accessControl}
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    setDocRegis({
                      ...docRegis,
                      accessControl: newValue
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label={t('projectDocument.documentLevel')}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <Autocomplete
                  className={classes.fullWidth}
                  id="document-creator"
                  options={users}
                  getOptionLabel={(option) =>
                    `${option?.firstName} ${option?.lastName}`
                  }
                  value={docRegis.documentCreator}
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    setDocRegis({
                      ...docRegis,
                      documentCreator: newValue
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label={t('projectDocument.documentCreator')}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid className={classes.textAlignRight} item xs={12} sm={5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={docRegis.isAIMS}
                      onChange={(event) => {
                        setDocRegis({
                          ...docRegis,
                          isAIMS: event.target.checked,
                          aimsDocumentNo: null
                        });
                      }}
                      color="primary"
                    />
                  }
                  label={t('projectDocument.requestFromAIMS')}
                  labelPlacement="start"
                />
              </Grid>
              <Grid
                component={Collapse}
                in={docRegis.isAIMS}
                item
                xs={12}
                sm={7}
              >
                <TextField
                  className={classes.fullWidth}
                  id="aims-document-no"
                  variant="outlined"
                  size="small"
                  label={t('projectDocument.aimsDocumentNo')}
                  value={docRegis.aimsDocumentNo ? docRegis.aimsDocumentNo : ''}
                  onChange={(event) => {
                    setDocRegis({
                      ...docRegis,
                      aimsDocumentNo:
                        event.target.value === '' ? null : event.target.value
                    });
                  }}
                  required={docRegis.isAIMS}
                />
              </Grid>
              <Grid item xs={12}>
                <Paper className={classes.additionalNoCard} variant="outlined">
                  <Typography variant="body2" color="textSecondary">
                    {t('projectDocument.additionalInformation')}
                  </Typography>
                  <Divider className={classes.divider} />
                  <div className={classes.additionalNoField}>
                    <TextField
                      className={classes.fullWidth}
                      id="client-document-no"
                      variant="outlined"
                      size="small"
                      label={t('projectDocument.clientDocumentNo')}
                      disabled={!docRegis.document}
                      value={
                        docRegis.clientDocumentNo
                          ? docRegis.clientDocumentNo
                          : ''
                      }
                      onChange={(event) => {
                        setDocRegis({
                          ...docRegis,
                          clientDocumentNo:
                            event.target.value === ''
                              ? null
                              : event.target.value
                        });
                      }}
                    />
                    <TextField
                      className={classes.fullWidth}
                      id="epc-document-no"
                      variant="outlined"
                      size="small"
                      label={t('projectDocument.epcDocumentNo')}
                      disabled={!docRegis.document}
                      value={
                        docRegis.epcDocumentNo ? docRegis.epcDocumentNo : ''
                      }
                      onChange={(event) => {
                        setDocRegis({
                          ...docRegis,
                          epcDocumentNo:
                            event.target.value === ''
                              ? null
                              : event.target.value
                        });
                      }}
                    />
                    <TextField
                      className={classes.fullWidth}
                      id="vendor-document-no"
                      variant="outlined"
                      size="small"
                      label={t('projectDocument.vendorDocumentNo')}
                      disabled={!docRegis.document}
                      value={
                        docRegis.vendorDocumentNo
                          ? docRegis.vendorDocumentNo
                          : ''
                      }
                      onChange={(event) => {
                        setDocRegis({
                          ...docRegis,
                          vendorDocumentNo:
                            event.target.value === ''
                              ? null
                              : event.target.value
                        });
                      }}
                    />
                  </div>
                </Paper>
              </Grid>
            </Grid>
          </Collapse>
          <Collapse in={activeStep > 0}>
            <Grid container spacing={0}>
              <Grid item xs={12} sm={5}>
                <Typography variant="subtitle2">{t('projectDocument.discipline')}</Typography>
                <Typography variant="body1" gutterBottom>
                  {docRegis?.discipline?.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Typography variant="subtitle2">{t('projectDocument.documentTemplate')}</Typography>
                <Typography variant="body1" gutterBottom>
                  {docRegis?.document?.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Typography variant="subtitle2">{t('projectDocument.documentNo')}</Typography>
                <Typography variant="body1" gutterBottom>
                  {docRegis?.no}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Typography variant="subtitle2">{t('projectDocument.documentName')}</Typography>
                <Typography variant="body1" gutterBottom>
                  {docRegis?.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Typography variant="subtitle2">{t('projectDocument.documentLevel')}</Typography>
                <Typography variant="body1" gutterBottom>
                  {docRegis?.accessControl?.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Typography variant="subtitle2">{t('projectDocument.documentCreator')}</Typography>
                <Typography variant="body1" gutterBottom>
                  {docRegis?.documentCreator?.firstName}{' '}
                  {docRegis?.documentCreator?.lastName}
                </Typography>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>
        {/* TODO: Discuss with Team to confirm that we should custom share in the create document page */}
        {/* <Collapse in={activeStep === 0 && docRegis.accessControl !== null}> */}
        <Collapse in={false}>
          <Paper className={classes.card}>
            <Typography variant="subtitle1" gutterBottom>
              {t('projectDocument.shareDocument')}
            </Typography>
            <Divider className={classes.divider} />
            <Alert severity="info">
              {t('projectDocument.shareDocumentInfo', { level: docRegis.accessControl?.name.toLowerCase() })}
            </Alert>
            <div className={classes.textAlignRight}>
              <FormControlLabel
                control={
                  <Switch
                    checked={customShare}
                    onChange={(event) => {
                      setCustomShare(event.target.checked);
                      if (!event.target.checked) {
                        setDocRegis({
                          ...docRegis,
                          customAccessControlUsers: []
                        });
                      }
                    }}
                    color="primary"
                  />
                }
                label={t('projectDocument.custom')}
                labelPlacement="start"
              />
            </div>
            <Collapse in={customShare}>
              <Autocomplete
                className={classes.fullWidth}
                id="custom-share-user"
                size="small"
                multiple
                options={users}
                getOptionLabel={(option) =>
                  `${option?.firstName} ${option?.lastName}`
                }
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={`${option?.firstName} ${option?.lastName}`}
                      {...getTagProps({ index })}
                      variant="outlined"
                      size="small"
                      deleteIcon={<ClearRounded />}
                    />
                  ))
                }
                value={docRegis.customAccessControlUsers}
                getOptionSelected={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                  setDocRegis({
                    ...docRegis,
                    customAccessControlUsers: newValue
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={
                      docRegis.customAccessControlUsers.length === 0
                        ? t('projectDocument.selectUser') as string
                        : ''
                    }
                    required={
                      customShare &&
                      docRegis.customAccessControlUsers.length === 0
                    }
                  />
                )}
              />
            </Collapse>
          </Paper>
        </Collapse>
        <Collapse in={activeStep === 0}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={loading}
            startIcon={<AddRounded />}
          >
            {t('projectDocument.addDocument')}
          </Button>
        </Collapse>
      </form>
      <Collapse in={activeStep >= 1}>
        <form onSubmit={onSubmitDocumentRevision}>
          <Paper className={classes.card}>
            <Typography variant="subtitle1" gutterBottom>
              {t('projectDocument.documentRevision')}
            </Typography>
            <Divider className={classes.divider} />
            <Alert className={classes.marginBottom} severity="info">
              {t('projectDocument.autoCalculateDate', { days: DAYS_FOR_COMPLETE_REVIEW })}
            </Alert>
            {revRegis.length === 0 ? (
              <div className={classes.fileNotFound}>{t('projectDocumentRevision.revisionNotFound')}</div>
            ) : (
              <Grid container spacing={2}>
                {revRegis.map((revision) => {
                  const currentRevision = purposes.find(
                    (purpose) => purpose.id === revision.purpose?.id
                  )?.revisions;
                  const revisionList = currentRevision ? currentRevision : [];
                  return (
                    <Grid item xs={12} sm={6} md={4} key={revision.tempId}>
                      <Grid
                        className={clsx(
                          classes.padding1,
                          classes.positionRelative,
                          classes.hoverFocus
                        )}
                        container
                        spacing={1}
                        component={Paper}
                        variant="outlined"
                      >
                        <Tooltip title={t('projectDocument.remove') as string} arrow>
                          <IconButton
                            className={classes.closeIcon}
                            size="small"
                            onClick={() =>
                              handleDeleteRevision(revision.tempId)
                            }
                          >
                            <CloseRounded />
                          </IconButton>
                        </Tooltip>
                        <Grid item xs={7}>
                          <Autocomplete
                            className={classes.fullWidth}
                            id={`purpose-${revision.tempId}`}
                            options={purposes}
                            getOptionLabel={(option) => option.abbreviation}
                            value={revision.purpose}
                            getOptionSelected={(option, value) =>
                              option.id === value.id
                            }
                            onChange={(event, newValue) =>
                              handleChangeValueById(
                                revision.tempId,
                                'purpose',
                                newValue
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                label={t('projectDocument.purpose')}
                                required
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <Autocomplete
                            className={classes.fullWidth}
                            id={`revision-${revision.tempId}`}
                            options={revisionList}
                            getOptionLabel={(option) => option?.name}
                            value={revision.revision}
                            getOptionSelected={(option, value) =>
                              option.id === value.id
                            }
                            onChange={(event, newValue) =>
                              handleChangeValueById(
                                revision.tempId,
                                'revision',
                                newValue
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                label={t('projectDocument.revision')}
                                required
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            className={classes.fullWidth}
                            type="number"
                            id={`man-hour-${revision.tempId}`}
                            size="small"
                            label={t('projectDocument.manHour')}
                            required
                            value={revision.manHour ? revision.manHour : ''}
                            onChange={(event) =>
                              handleChangeValueById(
                                revision.tempId,
                                'manHour',
                                event.target.value === '' ||
                                  Number.parseFloat(event.target.value) <= 0
                                  ? null
                                  : Number.parseFloat(event.target.value)
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={7}>
                          <KeyboardDatePicker
                            className={clsx(
                              classes.fullWidth,
                              classes.marginTopNone
                            )}
                            autoOk
                            size="small"
                            variant="inline"
                            InputAdornmentProps={{ position: 'end' }}
                            id={`issue-date-${revision.tempId}`}
                            label="Issue Date"
                            value={revision.planIssueDate}
                            onChange={(event) =>
                              handleChangeValueById(
                                revision.tempId,
                                'planIssueDate',
                                event
                              )
                            }
                            KeyboardButtonProps={{
                              'aria-label': t('projectDocument.changePlanIssueDate') as string
                            }}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <KeyboardDatePicker
                            className={clsx(
                              classes.fullWidth,
                              classes.marginTopNone
                            )}
                            autoOk
                            size="small"
                            variant="inline"
                            InputAdornmentProps={{ position: 'end' }}
                            id={`completed-date-${revision.tempId}`}
                            label="Completed Date"
                            value={revision.planCompletedDate}
                            onChange={(event) =>
                              handleChangeValueById(
                                revision.tempId,
                                'planCompletedDate',
                                event
                              )
                            }
                            KeyboardButtonProps={{
                              'aria-label': t('projectDocument.changeCompleteIssueDate') as string
                            }}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            className={classes.marginRightNone}
                            control={
                              <Checkbox
                                size="small"
                                color="primary"
                                disabled={
                                  revision.purpose?.id !== PurposeLabel.IFR
                                }
                                checked={revision.isAutoApproval}
                                onChange={(event) =>
                                  handleChangeValueById(
                                    revision.tempId,
                                    'isAutoApproval',
                                    event.target.checked
                                  )
                                }
                              />
                            }
                            label={t('projectDocument.autoApproval')}
                          />
                          <HelpTooltip>
                            {revision.purpose?.id === PurposeLabel.IFR
                              ? t('projectDocument.autoApprovalHelp')
                              : t('projectDocument.autoApprovalIfrOnly')}
                          </HelpTooltip>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            <Divider className={classes.dividerRevision} />
            <div className={classes.buttonNewRev}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setRevRegis([
                    ...revRegis,
                    { ...defaultRevision, tempId: tempId }
                  ]);
                  setTempId(tempId + 1);
                }}
                disabled={!createdProjectDocument}
              >
                {t('projectDocument.newRevision')}
              </Button>
            </div>
          </Paper>
          <Collapse in={activeStep <= 1}>
            <div className={classes.buttonStepOne}>
              <Button size="large" fullWidth onClick={onSkipDocumentRevision}>
                {t('projectDocument.skip')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={revRegis.length === 0 || loading}
              >
                {t('projectDocument.finish')}
              </Button>
            </div>
          </Collapse>
        </form>
      </Collapse>
    </div>
  );
}

export function checkProductType(product: string): ProductNameLabel | null {
  const epcGroup = [
    'EPC - Olefin',
    'EPC - PolyOlefin',
    'EPC - Vinyl & MRR',
    'EPC'
  ];
  const pmcGroup = [
    'PMC - Olefin',
    'PMC - PolyOlefin',
    'PMC - Vinyl & MRR',
    'PMC'
  ];
  if (epcGroup.some((epc) => epc === product)) return ProductNameLabel.epc;
  if (pmcGroup.some((pmc) => pmc === product)) return ProductNameLabel.pmc;
  return null;
}
